import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';

import { collection, onSnapshot, query, QuerySnapshot, DocumentData } from 'firebase/firestore';

import FullScreenDialog from './components/modal';
import { deleteHtmlFromString } from 'helpers/htmlParser';
import { useParams } from 'react-router-dom';
import { db } from '../../utils/config/firebase';

interface Column {
    id: 'id' | 'job_post_preview' | 'edit' | 'job_title';
    label: string;
}

const columns: readonly Column[] = [
    {
        id: 'id',
        label: 'Job-ID',
    },
    {
        id: 'job_title',
        label: 'Job Title',
    },
    {
        id: 'job_post_preview',
        label: 'Jobpost preview',
    },
    {
        id: 'edit',
        label: '',
    },
];

export interface Job {
    id: string;
    intake_note: string;
    job_post: string;
    job_post_preview: string;
    job_title?: string;
    edit?: boolean;
}

function createJobPostPreview(output: string) {
    let preview = deleteHtmlFromString(deleteHtmlFromString(output.split('>')?.join('> ')))
        .split(' ')
        .filter((word) => word !== '')
        .slice(0, 20)
        ?.join(' ');
    if (preview.split(' ').length === 20) {
        preview += '...';
    }
    return preview;
}

const createJob = (
    id: string,
    intake_note: string,
    job_post: string,
    job_title: string | undefined,
): Job => {
    const job_post_preview = createJobPostPreview(job_post);

    return {
        id,
        intake_note,
        job_post,
        job_post_preview,
        job_title, // can be undefined if not present in parsed_job_post
    };
};

const ModelTrainer: FC = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [rows, setRows] = useState<Job[]>([]);
    const params = useParams();
    const { collection_id } = params;
    const collectionPath = `/jobAdsTrainingData/${collection_id}/examples`;

    useEffect(() => {
        if (collection_id) {
            const q = query(collection(db, collectionPath));

            const unsubscribe = onSnapshot(
                q,
                (querySnapshot: QuerySnapshot<DocumentData>) => {
                    const data: Job[] = querySnapshot.docs.map((doc) => {
                        const docData = doc.data();
                        const jobTitle = docData?.parsed_job_post?.job_title;
                        return createJob(doc.id, docData.intake_note, docData.job_post, jobTitle);
                    });
                    setRows(data);
                },
                (error) => {
                    console.error('Error fetching collection: ', error);
                },
            );

            // Cleanup function to unsubscribe when the component unmounts
            return () => unsubscribe();
        }
    }, [collectionPath, collection_id]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    return (
        <>
            <Paper className="model-trainer-table" sx={{ width: '100%', overflow: 'hidden' }}>
                <FullScreenDialog />
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column.id}>{column.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => {
                                    return (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell
                                                        align={
                                                            column.id === 'edit' ? 'right' : 'left'
                                                        }
                                                        key={column.id}
                                                    >
                                                        {column.id === 'job_title' ? (
                                                            row.job_title ? (
                                                                row.job_title
                                                            ) : (
                                                                'please wait...'
                                                            )
                                                        ) : column.id === 'edit' ? (
                                                            <FullScreenDialog
                                                                editMode
                                                                initialIntakeNote={row.intake_note}
                                                                initialJobPost={row.job_post}
                                                                job={row}
                                                            />
                                                        ) : (
                                                            value
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </>
    );
};

export default ModelTrainer;
